/**
 * useSpecialEditorFieldDraft — the SHARED controller for a CC-3 special-editor
 * per-entity form (REVCLOUD-PROPS-EDITOR / HATCH-EDIT-DIALOG …).
 *
 * Every registered `SpecialEditorHost` form is the same shape: a controlled
 * draft over a set of descriptor-backed string fields, diffed against a
 * persisted baseline, emitting a MINIMAL `properties/apply` of only the changed
 * fields, with an entity-swap reset and a lift-to-host effect. That controller
 * is ONE domain truth — extracted here so each form supplies only its
 * field-specific parts (the baseline derivation + the validation) and never
 * re-implements the mechanics (dep-graph / dup gate: the forms previously
 * carried token-identical copies of this block).
 *
 * ПРИОРИТЕТ #1 — the form NEVER mutates geometry: `buildWriteBack` returns an
 * ordinary `properties/apply` `EditorCommand` the host dispatches through the
 * safe contour (validation → geometry reducer → audit → undo → regen). A draft
 * that fails the caller's `validate` ABORTS the apply (`buildWriteBack` → null),
 * so the host keeps OK open (AutoCAD reject-on-invalid).
 *
 * ## Contract
 *
 * - `baseline` is a `label → displayString` map (the descriptor labels the form
 *   reads/writes, at their current `resolveDisplay` value). The draft starts
 *   here and RESETS here whenever the host swaps in a freshly-resolved entity
 *   (a new object identity after a write-back / a different open target).
 * - `changedValues` is the minimal payload: each label whose TRIMMED draft value
 *   differs from the trimmed baseline (enum/boolean selects carry no whitespace,
 *   so the uniform trim is a no-op for them and normalizes free-text edits).
 * - `validate(changedValues)` returns `false` to abort the write (invalid input);
 *   pass a STABLE callback (`useCallback`) so the lifted `buildWriteBack` — and
 *   thus `onDraftChange` — does not churn every render.
 */

import { useCallback, useEffect, useMemo, useState } from "react";

import type { EditorCommand } from "@dxf-ai-editor/contracts";
import type { DrawingEntity } from "@dxf-ai-editor/drawing-model";

import type { SpecialEditorFormDraft } from "./SpecialEditorHost";

/** A special-editor form draft: descriptor-label → controlled string value. */
export type SpecialEditorDraftValues = Record<string, string>;

export interface UseSpecialEditorFieldDraftResult {
  /** the live controlled-input values (label → raw string). */
  draft: SpecialEditorDraftValues;
  /** merge a partial patch into the draft (input onChange handlers). */
  update: (patch: SpecialEditorDraftValues) => void;
  /** the minimal `label → value` map of ONLY the fields changed from baseline. */
  changedValues: SpecialEditorDraftValues;
  /** true while the draft differs from baseline (gates the host's Apply button). */
  dirty: boolean;
}

export function useSpecialEditorFieldDraft(
  entity: DrawingEntity,
  baseline: SpecialEditorDraftValues,
  onDraftChange: (draft: SpecialEditorFormDraft) => void,
  validate: (changedValues: SpecialEditorDraftValues) => boolean,
): UseSpecialEditorFieldDraftResult {
  const [draft, setDraft] = useState<SpecialEditorDraftValues>(baseline);

  // Re-derive the baseline when the host swaps in a freshly-resolved entity.
  // React "store info from previous render" pattern — adjust state during render
  // so there is no frame where the stale draft is live. `entity` is stable while
  // the user edits (no dispatch until Apply/OK), so this preserves in-progress
  // edits and only resets after a committed write / entity swap.
  const [renderedEntity, setRenderedEntity] = useState<DrawingEntity>(entity);
  if (renderedEntity !== entity) {
    setRenderedEntity(entity);
    setDraft(baseline);
  }

  // Only the fields that differ from baseline — an unchanged field is never
  // re-dispatched, so an incidental value can't clobber the stored one.
  const changedValues = useMemo<SpecialEditorDraftValues>(() => {
    const values: SpecialEditorDraftValues = {};
    for (const [label, base] of Object.entries(baseline)) {
      const next = (draft[label] ?? "").trim();
      if (next !== base.trim()) values[label] = next;
    }
    return values;
  }, [draft, baseline]);

  const dirty = Object.keys(changedValues).length > 0;

  const buildWriteBack = useCallback((): readonly EditorCommand[] | null => {
    if (Object.keys(changedValues).length === 0) return null;
    // Caller's field-specific validation — a reject keeps the host's OK open.
    if (!validate(changedValues)) return null;
    return [
      {
        type: "properties/apply",
        source: "toolbar",
        entityIds: [entity.id],
        values: changedValues,
      },
    ];
  }, [changedValues, entity.id, validate]);

  // Lift the current draft (dirty gate + write-back builder) to the host on every
  // change. The host owns Apply/OK; it calls `buildWriteBack` and records the
  // regen view-op.
  useEffect(() => {
    const lifted: SpecialEditorFormDraft = { dirty, buildWriteBack };
    onDraftChange(lifted);
  }, [dirty, buildWriteBack, onDraftChange]);

  const update = useCallback((patch: SpecialEditorDraftValues) => {
    setDraft((prev) => ({ ...prev, ...patch }));
  }, []);

  return { draft, update, changedValues, dirty };
}
